import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheck,
  FiHelpCircle,
  FiX,
} from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import FeatureValuePreview, { featureValueBenefits } from './FeatureValuePreview';
import {
  FocusedGuidePreview,
  OtherFeaturesPreview,
  SlidePreview,
} from './ProductTutorialPreviews';
import { PRODUCT_TUTORIAL_COPY } from './productTutorialCopy';
import { GUIDE_COPY, guideForPath, otherFeatureEntries } from './productTutorialGuides';

const TUTORIAL_VERSION = 1;

function storageKey(uid) {
  return `jobseeqr:product-tutorial:v${TUTORIAL_VERSION}:${uid}`;
}

function readLocalProgress(uid) {
  if (!uid || typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(storageKey(uid)) || '{}');
  } catch (_error) {
    return {};
  }
}

function ProductTutorial({ user, profile, onProfileSaved, helpTarget }) {
  const location = useLocation();
  const { language, isRTL } = useLanguage();
  const languageKey = language === 'ar' ? 'ar' : 'en';
  const copy = PRODUCT_TUTORIAL_COPY[languageKey];
  const guideCopy = GUIDE_COPY[languageKey];
  const savedProgress = useMemo(() => (
    profile?.productTutorial?.version === TUTORIAL_VERSION
      ? profile.productTutorial
      : {}
  ), [profile?.productTutorial]);
  const [progress, setProgress] = useState(() => ({ ...readLocalProgress(user?.uid), ...savedProgress }));
  const [manualWelcome, setManualWelcome] = useState(() => (
    process.env.NODE_ENV !== 'production'
    && new URLSearchParams(window.location.search).get('tutorialPreview') === '1'
  ));
  const [manualGuideKey, setManualGuideKey] = useState('');
  const [slideIndex, setSlideIndex] = useState(0);
  const [guidePage, setGuidePage] = useState(0);
  const welcomeOpen = manualWelcome || !progress.welcomeCompleted;
  const forcedGuide = process.env.NODE_ENV !== 'production'
    ? new URLSearchParams(location.search).get('tutorialGuidePreview') || ''
    : '';
  const routeGuideKey = useMemo(() => guideForPath(location.pathname), [location.pathname]);
  const guideKey = manualGuideKey || forcedGuide || routeGuideKey;
  const activeGuide = !welcomeOpen && guideKey && (manualGuideKey || forcedGuide || !progress.guides?.[guideKey])
    ? guideCopy.guides[guideKey]
    : null;
  const guideUsesModal = Boolean(activeGuide?.preview || manualGuideKey || forcedGuide);

  useEffect(() => {
    setProgress((current) => ({ ...current, ...savedProgress }));
  }, [savedProgress]);

  useEffect(() => {
    const modalOpen = welcomeOpen || guideUsesModal;
    document.body.classList.toggle('js-tutorial-open', modalOpen);
    return () => document.body.classList.remove('js-tutorial-open');
  }, [guideUsesModal, welcomeOpen]);

  useEffect(() => {
    setGuidePage(0);
  }, [guideKey]);

  const persist = (next, updates) => {
    setProgress(next);
    if (user?.uid) window.localStorage.setItem(storageKey(user.uid), JSON.stringify(next));
    if (onProfileSaved) {
      onProfileSaved({
        'productTutorial/version': TUTORIAL_VERSION,
        ...updates,
      }).catch((error) => console.error('Unable to save tutorial progress:', error));
    }
  };

  const completeWelcome = () => {
    const next = { ...progress, version: TUTORIAL_VERSION, welcomeCompleted: true };
    persist(next, { 'productTutorial/welcomeCompleted': true });
    setManualWelcome(false);
    setSlideIndex(0);
  };

  const dismissGuide = () => {
    if (manualGuideKey) {
      setManualGuideKey('');
      setGuidePage(0);
      return;
    }
    const nextGuides = { ...(progress.guides || {}), [guideKey]: true };
    const next = { ...progress, version: TUTORIAL_VERSION, guides: nextGuides };
    persist(next, { [`productTutorial/guides/${guideKey}`]: true });
  };

  const slide = copy.slides[slideIndex];
  const SlideIcon = slide.icon;
  const GuideIcon = activeGuide?.icon;
  const focusedGuidePages = activeGuide?.preview
    ? featureValueBenefits(activeGuide.preview, language).map(([title, body], page) => ({
      title,
      body,
      featurePreview: activeGuide.preview,
      featurePage: page,
    }))
    : activeGuide?.pages || (activeGuide ? [{
      title: activeGuide.title,
      body: activeGuide.body,
      previewKind: activeGuide.previewKind,
    }] : []);
  const guidePages = activeGuide
    ? [...focusedGuidePages, {
      title: guideCopy.otherFeaturesTitle,
      body: guideCopy.otherFeaturesBody,
      otherFeatures: true,
    }]
    : [];
  const currentGuidePage = guidePages[guidePage];
  const otherFeaturesPage = Boolean(currentGuidePage?.otherFeatures);
  const remainingFeatures = activeGuide
    ? otherFeatureEntries(guideCopy, [activeGuide.featureKey, ...(activeGuide.excludedFeatureKeys || [])])
    : [];
  const BackIcon = isRTL ? FiArrowRight : FiArrowLeft;
  const NextIcon = isRTL ? FiArrowLeft : FiArrowRight;
  const helpButton = <button type="button" className="js-tutorial-help" aria-label={copy.help} onClick={() => {
    setGuidePage(0);
    setManualGuideKey(routeGuideKey);
  }}><FiHelpCircle aria-hidden="true" /><span>{copy.help}</span></button>;

  return (
    <>
      {!welcomeOpen && !activeGuide && routeGuideKey && (
        helpTarget ? createPortal(helpButton, helpTarget) : helpButton
      )}

      {welcomeOpen && (
        <div className="js-tutorial-backdrop">
          <section className="js-tutorial-welcome" role="dialog" aria-modal="true" aria-labelledby="tutorial-title">
            <button type="button" className="js-tutorial-welcome__close" onClick={completeWelcome} aria-label={copy.close}>
              <FiX aria-hidden="true" />
            </button>
            <div className="js-tutorial-welcome__preview" aria-hidden="true">
              <SlidePreview index={slideIndex} copy={copy} Icon={SlideIcon} />
            </div>
            <div className="js-tutorial-welcome__copy">
              <p className="js-tutorial-welcome__eyebrow">{slide.eyebrow}</p>
              <h2 id="tutorial-title">{slide.title}</h2>
              <p className="js-tutorial-welcome__body">{slide.body}</p>
              <ul>{slide.points.map((point) => <li key={point}><FiCheck aria-hidden="true" /> {point}</li>)}</ul>
            </div>
            <footer>
              <div className="js-tutorial-welcome__progress">
                <span>{copy.step.replace('{current}', slideIndex + 1).replace('{total}', copy.slides.length)}</span>
                <div>{copy.slides.map((item, index) => <i key={item.title} className={index === slideIndex ? 'is-active' : ''} />)}</div>
              </div>
              <div className="js-tutorial-welcome__actions">
                {slideIndex === 0
                  ? <button type="button" className="is-text" onClick={completeWelcome}>{copy.skip}</button>
                  : <button type="button" className="is-secondary" onClick={() => setSlideIndex((current) => current - 1)}><BackIcon /> {copy.back}</button>}
                <button type="button" className="is-primary" onClick={() => {
                  if (slideIndex === copy.slides.length - 1) completeWelcome();
                  else setSlideIndex((current) => current + 1);
                }}>
                  {slideIndex === copy.slides.length - 1 ? copy.finish : copy.next}
                  {slideIndex < copy.slides.length - 1 && <NextIcon aria-hidden="true" />}
                </button>
              </div>
            </footer>
          </section>
        </div>
      )}

      {activeGuide && (
        guideUsesModal ? (
          <div className="js-tutorial-backdrop is-feature-tour">
            <section className="js-tutorial-feature-tour" role="dialog" aria-modal="true" aria-labelledby="feature-tour-title">
              <button type="button" className="js-tutorial-welcome__close" onClick={dismissGuide} aria-label={copy.close}><FiX /></button>
              <div className="js-tutorial-feature-tour__preview">
                {otherFeaturesPage
                  ? <OtherFeaturesPreview entries={remainingFeatures} />
                  : currentGuidePage.featurePreview
                    ? <FeatureValuePreview type={currentGuidePage.featurePreview} page={currentGuidePage.featurePage} />
                    : <FocusedGuidePreview kind={currentGuidePage.previewKind} copy={copy} Icon={GuideIcon} />}
              </div>
              <div className="js-tutorial-feature-tour__copy">
                <span className="js-tutorial-guide__icon" aria-hidden="true"><GuideIcon /></span>
                <h2 id="feature-tour-title">{currentGuidePage.title}</h2>
                <p>{currentGuidePage.body}</p>
              </div>
              <footer>
                <div className="js-tutorial-welcome__progress">
                  <span>{copy.step.replace('{current}', guidePage + 1).replace('{total}', guidePages.length)}</span>
                  <div>{guidePages.map(({ title }, index) => <i key={title} className={index === guidePage ? 'is-active' : ''} />)}</div>
                </div>
                <div className="js-tutorial-welcome__actions">
                  {guidePage > 0 && <button type="button" className="is-secondary" onClick={() => setGuidePage((current) => current - 1)}><BackIcon /> {copy.back}</button>}
                  <button type="button" className="is-primary" onClick={() => {
                    if (guidePage === guidePages.length - 1) dismissGuide();
                    else setGuidePage((current) => current + 1);
                  }}>
                    {guidePage === guidePages.length - 1 ? copy.gotIt : copy.next}
                    {guidePage < guidePages.length - 1 && <NextIcon />}
                  </button>
                </div>
              </footer>
            </section>
          </div>
        ) : (
          <aside className="js-tutorial-guide" aria-live="polite">
            <span className="js-tutorial-guide__icon" aria-hidden="true"><GuideIcon /></span>
            <div><strong>{currentGuidePage.title}</strong><p>{currentGuidePage.body}</p></div>
            <button type="button" onClick={dismissGuide}>{copy.gotIt}</button>
          </aside>
        )
      )}
    </>
  );
}

export default ProductTutorial;
